/**
 * Attachment merge utilities for import operations
 * 
 * This module provides functions for merging existing and imported attachments,
 * handling filename conflicts, and ensuring data integrity during import operations.
 */

import { SourceDatabase } from '../note'

/**
 * Merge existing attachments with newly imported files
 * 
 * Uses a union strategy to keep both existing and new attachments,
 * resolving filename conflicts with UUID prefixing to avoid data loss.
 * 
 * @param existing - Array of existing attachment references (filenames or URLs)
 * @param imported - Array of newly imported File objects
 * @param _backend - Source database type (affects merge strategy)
 * @returns Merged array of attachment references
 */
export function mergeAttachments(
  existing: string[], 
  imported: File[], 
  _backend: SourceDatabase
): string[] {
  const oldList = existing
  const newList = imported.map((f) => f.name)

  // Union strategy: keep both old and new, resolve conflicts with UUID
  const merged = [...oldList]
  for (const newFile of newList) {
    if (!oldList.some((old) => filename(old) === newFile)) {
      merged.push(newFile)
    }
  }

  // TODO: Consider implementing orphaned attachment cleanup in the future
  // Current approach: Keep union to avoid data loss
  return merged
}

/**
 * Extract filename from attachment reference
 * 
 * Handles both simple filenames and URL-based references by extracting
 * the last path component.
 * 
 * @param attachmentReference - Attachment reference (filename or URL)
 * @returns Extracted filename
 */
function filename(attachmentReference: string): string {
  return attachmentReference.split('/').pop() ?? attachmentReference
}

/**
 * Resolve filename conflicts by adding UUID prefixes to duplicate names
 * 
 * This ensures unique filenames when multiple files have the same name.
 * Uses 8-character UUID prefixes to maintain readability while ensuring uniqueness.
 * 
 * @param files - Array of File objects that may have duplicate names
 * @returns Array of File objects with unique names
 */
export function resolveFilenameConflicts(files: File[]): File[] {
  const seen = new Set<string>()
  return files.map((file) => {
    if (seen.has(file.name)) {
      const uuid = crypto.randomUUID().slice(0, 8)
      const extension = file.name.split('.').pop()
      const base = file.name.replace(/\.[^./]+$/, '')
      const newName = extension ? `${base}_${uuid}.${extension}` : `${base}_${uuid}`
      return new File([file], newName, { type: file.type })
    }
    seen.add(file.name)
    return file
  })
}
